@if (loading) {
    <div class="fancy-loader"></div>
} @else {
    <cerrix-workflow-bar [steps]="workflowSteps"></cerrix-workflow-bar>
    <div class="risk-generate-ai">
        <div class="risk-generate-ai-main">
            <div class="header">
                <div class="title">
                    <ng-container [ngTemplateOutlet]="stepTitle"></ng-container>
                </div>
                <cerrix-instructions-icon
                    (click)="cerrixSlideOver.show()"
                ></cerrix-instructions-icon>
            </div>
            <div class="risk-generate-ai-content">
                <div class="subtitle">
                    <ng-container [ngTemplateOutlet]="stepSubtitle"></ng-container>
                </div>
                <ng-container [ngTemplateOutlet]="stepControls"></ng-container>
                <ng-container [ngTemplateOutlet]="stepContent"></ng-container>
            </div>
        </div>
        <div class="risk-generate-ai-source-documents">
            <div
                class="documents-header"
                [class.open]="sourceDocumentsOpen"
                (click)="sourceDocumentsOpen = !sourceDocumentsOpen"
                data-uiid="risk-creai-source-documents-header"
            >
                <div class="documents-title">Source Documents</div>
                <mat-icon
                    class="icon-documents-dropdown"
                    [svgIcon]="sourceDocumentsOpen ? 'arrow-up' : 'arrow-down'"
                ></mat-icon>
            </div>
            @if (sourceDocumentsOpen) {
                <div class="documents-list">
                    @for (document of sourceDocuments; track document.guid) {
                        <div class="document">
                            <div class="document-title">
                                {{ document.name }}
                            </div>
                            <div class="document-actions">
                                <mat-icon
                                    class="icon-download"
                                    [svgIcon]="'download'"
                                    (click)="sourceDocumentDownload(document.guid)"
                                    data-uiid="risk-creai-source-download"
                                ></mat-icon>
                            </div>
                        </div>
                    }
                </div>
            }
        </div>
    </div>
    <ng-container [ngTemplateOutlet]="stepFooter"></ng-container>

    <cerrix-slide-over #cerrixSlideOver>
        <ng-template #title>
            <div class="paragraph">
                <div class="header">Generate and add risks & controls</div>
                <div class="content">
                    By uploading a document, AI was able to generate risks with some controls linked
                    to that risk. In this overview you are able to view, edit and add the generated
                    risks and controls to your CERRIX database.
                </div>
            </div>
        </ng-template>
        <ng-template #content>
            <div class="paragraph">
                <div class="header">View and edit details on generated risks & controls</div>
                <div class="content">
                    <p>
                        When you hover with your mouse over the options on your screen, you will see
                        that the risks and controls will show a blue color. Click on the risk or
                        control to make the item expand, and view more details.
                    </p>
                    <p>
                        In this overview, you are able to edit the data that was generated by AI.
                        Simply click on the input fields and edit or add your own preferred data.
                    </p>
                    <p>
                        IMPORTANT: if you edit or add data to a risk or control, it will not be
                        automatically added to your database. This requires an extra step explained
                        in the next paragraph.
                    </p>
                </div>
            </div>
            <div class="paragraph">
                <div class="header">
                    Marking risks and controls to create them in your CERRIX database
                </div>
                <div class="content">
                    <p>
                        Every generated risk and control has two buttons on the right side of the
                        element. One displaying a checkmark, the other an 'X'.
                    </p>
                    <p>
                        By clicking on the checkmark, you marked the risk or control to be added to
                        your CERRIX environment. If you click on the 'X' symbol, the element will be
                        marked 'rejected', and will not be added to your database. If you have not
                        marked the item, it will also not be added to your database.
                    </p>
                </div>
            </div>
            <div class="paragraph">
                <div class="header">Create new risks and controls</div>
                <div class="content">
                    <p>
                        When you are done marking the generated risks and controls, you can click on
                        the blue button 'Create new risks & controls' on the down right corner of
                        your screen.
                    </p>
                    <p>
                        On the left side of the button you will see how many new risks and controls
                        will be made, according to the items that you have marked.
                    </p>
                </div>
            </div>
        </ng-template>
    </cerrix-slide-over>
}

<ng-template #stepTitle>
    @switch (currentStepId()) {
        @case (workflowGenerate) {
            Generating risks & controls
        }
        @case (workflowSelect) {
            Select risks & controls
        }
        @case (workflowRefine) {
            Refine risks and related controls
        }
        @case (workflowComplete) {
            Created Risks and Controls
        }
    }
</ng-template>

<ng-template #stepSubtitle>
    @switch (currentStepId()) {
        @case (workflowGenerate) {
            Extracted risks and related controls
            <cerrix-instructions-icon
                [showText]="false"
                instructionText="The AI is working on the creation of suggestions for possible risks and controls, based on the submitted file(s). When the AI is done, all items will be displayed on this page."
            ></cerrix-instructions-icon>
        }
        @case (workflowSelect) {
            Extracted risks and related controls
            <cerrix-instructions-icon
                [showText]="false"
                instructionText="AI has extracted the following risks from the submitted document and provided for each risk a few suggested controls to cover the risk."
            ></cerrix-instructions-icon>
        }
        @case (workflowRefine) {
            Add information to risks and related controls
            <cerrix-instructions-icon
                [showText]="false"
                instructionText="AI has extracted the following risks from the submitted document and provided for each risk a few suggested controls to cover the risk."
            ></cerrix-instructions-icon>
        }
    }
</ng-template>

<ng-template #stepControls>
    <div class="page-controls">
        @if (currentStepId() !== workflowGenerate && !completedWithoutOutput) {
            @if (hasClosedRisks()) {
                <cerrix-button
                    buttonType="secondary"
                    secondaryColor="outlined-gray"
                    buttonSize="small"
                    (click)="showAllControls()"
                    data-uiid="risk-generate-ai-create-selection--show-controls"
                >
                    <mat-icon class="show-hide-icon" [svgIcon]="'show'"></mat-icon>Show controls
                </cerrix-button>
            } @else {
                <cerrix-button
                    buttonType="secondary"
                    secondaryColor="outlined-gray"
                    buttonSize="small"
                    (click)="hideAllControls()"
                    data-uiid="risk-generate-ai-create-selection--hide-controls"
                >
                    <mat-icon class="show-hide-icon" [svgIcon]="'hide'"></mat-icon>Hide controls
                </cerrix-button>
            }
        }
        @if (completedWithoutOutput) {
            <div class="completed-empty-warning">No risks or controls created</div>
        }
        @if (currentStepId() !== workflowGenerate && !completedWithoutOutput) {
            <div class="page-navigation">
                <app-cerrix-pagination
                    [totalItems]="risks().length"
                    [(currentPage)]="currentPage"
                    [itemsPerPage]="itemsPerPage()"
                ></app-cerrix-pagination>
                <app-cerrix-show-total-items
                    itemsText="risks"
                    [dropdownOptions]="[
                        { label: '3', value: '3' },
                        { label: '5', value: '5' },
                        { label: '10', value: '10' },
                    ]"
                    [dropdownSelectedIndex]="[3, 5, 10].indexOf(itemsPerPage())"
                    (dropdownSelectionChange)="itemsPerPage.set($event)"
                ></app-cerrix-show-total-items>
            </div>
        }
    </div>
</ng-template>

<ng-template #stepContent>
    @if (currentStepId() === workflowGenerate) {
        <div class="loading-container">
            <div class="loading-text">
                This process can take a while, please come back later to view the results
            </div>
            <div class="fancy-loader submodule"></div>
            <div class="loading-text">{{ creaiLoadingMinutes() }}</div>
        </div>
    } @else {
        <div class="risks-controls-container">
            @for (risk of paginatedRisks(); track risk.id; let riskIndex = $index) {
                <div
                    class="risk-row"
                    [class.expanded]="isRiskOpen(risk.id)"
                    [class.no-expander]="!(risk.controls?.length > 0)"
                >
                    <div class="row-expander">
                        @if (risk.controls?.length > 0) {
                            <mat-icon
                                class="expander-icon"
                                [data-uiid]="
                                    'risk-generate-ai-create-selection--expander-' + riskIndex
                                "
                                [svgIcon]="isRiskOpen(risk.id) ? 'arrow-up' : 'arrow-down'"
                                (click)="toggleControlsVisibility(risk.id)"
                            ></mat-icon>
                        }
                    </div>

                    <div
                        class="risk-controls-container"
                        [class.expandable]="currentStepId() === workflowSelect"
                    >
                        @if (
                            currentStepId() === workflowSelect ||
                            (currentStepId() === workflowRefine && risk.selected === true) ||
                            (currentStepId() === workflowComplete && risk.isDoneProcessing === true)
                        ) {
                            <div
                                class="risk-container"
                                [class.clickable]="currentStepId() === workflowSelect"
                                [data-uiid]="'risk-generate-ai-create-selection--risk-' + riskIndex"
                                [class.open]="isRiskContentOpen(risk.id)"
                            >
                                <header class="risk-header" (click)="toggleRiskContent(risk.id)">
                                    <div class="risk-title">
                                        {{ risk.name }}
                                    </div>
                                    <div class="risk-action">
                                        @if (currentStepId() === workflowSelect) {
                                            <cerrix-accept-reject-toggle
                                                [selection]="risk.selected"
                                                (selectionChange)="
                                                    updateRiskSelected(risk.id, $event)
                                                "
                                            ></cerrix-accept-reject-toggle>
                                        }
                                        @if (currentStepId() === workflowRefine) {
                                            @if (risk.isDoneProcessing) {
                                                <cerrix-icon-button
                                                    buttonType="primary"
                                                    buttonSize="small"
                                                    primaryColor="green"
                                                    data-uiid="risk-generate-ai-create-selection--edit-risk-button"
                                                    (click)="openCreatedRisk(risk)"
                                                    text="Risk saved"
                                                    icon="check"
                                                    iconPosition="right"
                                                />
                                            } @else {
                                                <cerrix-icon-button
                                                    buttonType="secondary"
                                                    buttonSize="small"
                                                    data-uiid="risk-generate-ai-create-selection--edit-risk-button"
                                                    (click)="openNewRisk(risk.id)"
                                                    text="Edit risk"
                                                    icon="link-external"
                                                    iconPosition="right"
                                                />
                                            }
                                        }
                                        @if (currentStepId() === workflowComplete) {
                                            <mat-icon
                                                svgIcon="link-external"
                                                (click)="openCreatedRisk(risk)"
                                                [class.clickable]="true"
                                            />
                                        }
                                    </div>
                                </header>

                                <div class="risk-content">
                                    <div class="risk-metadata">
                                        <div class="risk-metadata-document">
                                            Extracted from {{ processedDocument.document.name }}
                                        </div>
                                        <div class="risk-metadata-snippet">
                                            &lt;text snippet&gt; {{ getChunkData(risk.chunkId) }}
                                        </div>
                                    </div>
                                    <cerrix-input-group
                                        [data-uiid]="
                                            'risk-generate-ai-create-selection--risk-name-' +
                                            riskIndex
                                        "
                                        [name]="'risk-' + risk.id + '-name'"
                                        [(value)]="risk.name"
                                        >Name</cerrix-input-group
                                    >
                                    <cerrix-textarea-group
                                        [data-uiid]="
                                            'risk-generate-ai-create-selection--risk-description-' +
                                            riskIndex
                                        "
                                        [name]="'risk-' + risk.id + '-description'"
                                        textareaDialogTitle="Description"
                                        [rows]="5"
                                        [(value)]="risk.description"
                                        (valueChange)="risk.description = $event"
                                        >Description</cerrix-textarea-group
                                    >
                                </div>
                            </div>
                        } @else {
                            @if (risk.hasLinkedRisks === true) {
                                <cerrix-banner
                                    type="success"
                                    message="Linked to one or more risks"
                                ></cerrix-banner>
                            } @else {
                                @if (currentStepId() === workflowRefine) {
                                    <cerrix-banner
                                        type="warning"
                                        message="Make sure to link a risk to these controls in the control detail screen"
                                    ></cerrix-banner>
                                }
                                @if (currentStepId() === workflowComplete) {
                                    <cerrix-banner
                                        type="warning"
                                        message="No risks linked to this control"
                                    ></cerrix-banner>
                                }
                            }
                        }

                        @if (risk.controls?.length > 0) {
                            <section
                                class="controls-container"
                                [class.expanded]="isRiskOpen(risk.id)"
                                [class.expandable]="currentStepId() === workflowSelect"
                            >
                                @for (
                                    control of risk.controls;
                                    track control.id;
                                    let controlIndex = $index
                                ) {
                                    <div
                                        class="control-row"
                                        [class.clickable]="currentStepId() === workflowSelect"
                                        [data-uiid]="
                                            'risk-generate-ai-create-selection--control-' +
                                            riskIndex +
                                            '-' +
                                            controlIndex
                                        "
                                        [class.open]="isControlContentOpen(control.id)"
                                    >
                                        <header
                                            class="control-header"
                                            (click)="toggleControlContent(control.id)"
                                        >
                                            <div class="control-title">
                                                {{ control.name }}
                                            </div>
                                            <div class="control-action">
                                                @if (currentStepId() === workflowSelect) {
                                                    <cerrix-accept-reject-toggle
                                                        [selection]="control.selected"
                                                        (selectionChange)="
                                                            updateControlSelected(
                                                                risk.id,
                                                                control.id,
                                                                $event
                                                            )
                                                        "
                                                    ></cerrix-accept-reject-toggle>
                                                }
                                                @if (currentStepId() === workflowRefine) {
                                                    @if (control.isDoneProcessing) {
                                                        <cerrix-icon-button
                                                            buttonType="primary"
                                                            buttonSize="small"
                                                            primaryColor="green"
                                                            data-uiid="risk-generate-ai-create-selection--saved-control-button"
                                                            (click)="openCreatedControl(control)"
                                                            text="Control saved"
                                                            icon="check"
                                                            iconPosition="right"
                                                        />
                                                    } @else {
                                                        <cerrix-icon-button
                                                            buttonType="secondary"
                                                            buttonSize="small"
                                                            data-uiid="risk-generate-ai-create-selection--edit-control-button"
                                                            (click)="openNewControl(control.id)"
                                                            text="Edit control"
                                                            icon="link-external"
                                                            iconPosition="right"
                                                        />
                                                    }
                                                }
                                                @if (currentStepId() === workflowComplete) {
                                                    <mat-icon
                                                        svgIcon="link-external"
                                                        (click)="openCreatedControl(control)"
                                                        [class.clickable]="true"
                                                    />
                                                }
                                            </div>
                                        </header>

                                        <div class="control-content">
                                            <div class="control-metadata">
                                                <div class="control-metadata-document">
                                                    Extracted from
                                                    {{ processedDocument.document.name }}
                                                </div>
                                                <div class="control-metadata-snippet">
                                                    &lt;text snippet&gt;
                                                    {{ getChunkData(control.chunkId) }}
                                                </div>
                                            </div>
                                            <cerrix-input-group
                                                [data-uiid]="
                                                    'risk-generate-ai-create-selection--control-name-' +
                                                    riskIndex +
                                                    '-' +
                                                    controlIndex
                                                "
                                                [name]="'control-' + control.id + '-name'"
                                                [(value)]="control.name"
                                                >Name</cerrix-input-group
                                            >
                                            <cerrix-textarea-group
                                                [data-uiid]="
                                                    'risk-generate-ai-create-selection--control-description-' +
                                                    riskIndex +
                                                    '-' +
                                                    controlIndex
                                                "
                                                [name]="'control-' + control.id + '-description'"
                                                textareaDialogTitle="Description"
                                                [rows]="5"
                                                [(value)]="control.description"
                                                (valueChange)="control.description = $event"
                                                >Description</cerrix-textarea-group
                                            >
                                        </div>
                                    </div>
                                }
                            </section>
                        }
                    </div>
                </div>
            }
        </div>
    }
</ng-template>

<ng-template #stepFooter>
    @if (currentStepId() === workflowSelect) {
        <cerrix-page-footer>
            <div class="gen-ai-footer">
                <cerrix-button
                    buttonType="secondary"
                    buttonSize="big"
                    [waitingOnResult]="savingGeneratedRisksControls"
                    [showResultText]="generatedRisksControlsSaved"
                    data-uiid="risk-generate-ai-create-selection--save-selection"
                    (click)="saveClick()"
                    ><span button-text>Save<mat-icon svgIcon="check"></mat-icon></span
                    ><span result-text>Saved<mat-icon svgIcon="check"></mat-icon></span
                ></cerrix-button>
                <div class="counter-create-risks-controls-container">
                    <span class="risk-control-counter"
                        >{{ risksCount() }} risks and {{ controlsCount() }} controls selected</span
                    >
                    <cerrix-button
                        buttonType="primary"
                        buttonSize="big"
                        data-uiid="risk-generate-ai-create-selection--create-risks-controls"
                        [disabled]="risksCount() === 0 && controlsCount() === 0"
                        (click)="finishSelectionWorkflow()"
                    >
                        Refine risks & controls
                    </cerrix-button>
                </div>
            </div>
        </cerrix-page-footer>
    }
    @if (currentStepId() === workflowRefine) {
        <cerrix-page-footer>
            <div class="gen-ai-footer refine-step">
                <div class="counter-create-risks-controls-container">
                    <span class="risk-control-counter"
                        >{{ finishedRisksCount() }} risks and {{ finishedControlsCount() }} controls
                        completed</span
                    >
                    <cerrix-button
                        buttonType="secondary"
                        buttonSize="big"
                        data-uiid="risk-generate-ai-create-selection--mark-completed"
                        (click)="finishCreationWorkflow()"
                    >
                        Mark as completed
                    </cerrix-button>
                </div>
            </div>
        </cerrix-page-footer>
    }
</ng-template>

<cerrix-confirm-dialog
    [name]="unselectedConfirmDialogName"
    dataUiid="generate-risk-selection-confirm"
    icon="warning"
    title="Missing review"
    description="Some of the risks or controls have not been reviewed yet. If they're not marked with a checkmark, the suggestions will be deleted. Are you sure you want to continue?"
    cancelText="Continue reviewing"
    (cancel)="closeConfirmDialogs()"
    confirmText="Refine risks & controls"
    (confirm)="handleUpdateWorkflow()"
    [waitingOnResult]="updatingWorkflow"
    [showResultText]="workflowUpdated"
    class="dialog-unselected-confirm"
>
</cerrix-confirm-dialog>
<cerrix-confirm-dialog
    [name]="itemRejectedConfirmDialogName"
    dataUiid="generate-risk-selection-confirm"
    title="Continue with selected risks and controls"
    description="By completing this action, the risks and controls marked with 'X' will be removed. Are you sure you want to continue?"
    cancelText="Back"
    (cancel)="closeConfirmDialogs()"
    confirmText="Refine risks & controls"
    (confirm)="handleUpdateWorkflow()"
    [waitingOnResult]="updatingWorkflow"
    [showResultText]="workflowUpdated"
>
</cerrix-confirm-dialog>
<cerrix-confirm-dialog
    [name]="itemIncompleteConfirmDialogName"
    dataUiid="generate-risk-selection-confirm"
    icon="warning"
    title="Missing risks and controls"
    description="There are risks and/ or controls not yet added to your environment. Are you sure you want to continue without adding these items?"
    cancelText="Cancel"
    (cancel)="closeConfirmDialogs()"
    confirmText="Complete workflow"
    (confirm)="handleUpdateWorkflow()"
    [waitingOnResult]="updatingWorkflow"
    [showResultText]="workflowUpdated"
    class="dialog-unselected-confirm"
>
</cerrix-confirm-dialog>
